// Cierre nocturno — Hotel → Operación → Cierre nocturno.
// Sella el día de hotel: cobra al folio las noches de los huéspedes en casa,
// vence depósitos tentativos, marca no-shows y avanza la fecha de hotel.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, CalendarCheck, CheckCircle2, Loader2, LogIn, LogOut, Moon, type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import {
  CierreResumen, HotelDashboard, Modal, QK, SortTh, fmtFecha, hotelRpc, hotelTabla,
  money, num, useSort,useDialogoEnLinea } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Auditoria { id: string; fecha: string; ejecutada_at: string; resumen: CierreResumen }

export function CierreNocturnoHotel() {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const [errorVencidas, setErrorVencidas] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [resultado, setResultado] = useState<CierreResumen | null>(null);

  const { data: dash } = useQuery<HotelDashboard>({
    queryKey: [QK, 'dashboard'],
    queryFn: () => hotelRpc<HotelDashboard>('dashboard'),
    staleTime: 0, refetchOnMount: 'always',
  });

  const { data: historial = [], isFetching: cargandoHist } = useQuery<Auditoria[]>({
    queryKey: [QK, 'auditorias'],
    queryFn: () => hotelTabla<Auditoria>('auditorias', 'order=fecha.desc&limit=30'),
  });

  const sort = useSort<Auditoria>(historial, (row, key) => {
    switch (key) {
      case 'fecha': return row.fecha;
      case 'ocupacion': return row.resumen.ocupacion_pct;
      case 'adr': return row.resumen.adr;
      case 'revpar': return row.resumen.revpar;
      case 'ingreso': return row.resumen.ingreso_hospedaje;
      case 'noshows': return row.resumen.no_shows;
      default: return null;
    }
  }, 'fecha', 'desc');

  const cerrar = useMutation({
    mutationFn: (forzar: boolean) => hotelRpc<CierreResumen>('cierre_nocturno', { _forzar: forzar }),
    onSuccess: (r) => {
      setResultado(r); setErrorVencidas(null); setConfirmando(false);
      toast.success(`Cierre del ${fmtFecha(r.fecha)} ejecutado correctamente`);
      void qc.invalidateQueries({ queryKey: [QK] });
      void qc.invalidateQueries({ queryKey: [QK, 'dashboard'] });
      void qc.invalidateQueries({ queryKey: [QK, 'auditorias'] });
    },
    onError: (e) => {
      const msg = getErrorMessage(e);
      setConfirmando(false);
      if (/antes del cierre/i.test(msg)) setErrorVencidas(msg);
      else toast.error(msg);
    },
  });

  if (!dash) {
    return (
      <div className="flex items-center gap-2 p-6 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
      </div>
    );
  }

  const totales = historial.reduce((a, r) => ({
    ingreso: a.ingreso + Number(r.resumen.ingreso_hospedaje || 0),
    noshows: a.noshows + Number(r.resumen.no_shows || 0),
    ocup: a.ocup + Number(r.resumen.ocupacion_pct || 0),
    adr: a.adr + Number(r.resumen.adr || 0),
    revpar: a.revpar + Number(r.resumen.revpar || 0),
  }), { ingreso: 0, noshows: 0, ocup: 0, adr: 0, revpar: 0 });
  const n = historial.length || 1;

  return (
    <div className="space-y-4 p-6">
      {dlg.ui}
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold"><Moon className="h-5 w-5" /> Cierre nocturno</h1>
        <p className="text-sm text-muted-foreground">
          Sella el día de hotel: cobra al folio las noches de los huéspedes en casa, vence depósitos
          pendientes, marca no-shows y avanza la fecha del hotel al día siguiente.
        </p>
        <EstadoCierreAuto />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Checklist del {fmtFecha(dash.fecha_hotel)}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Checklist icon={LogIn} label="Llegadas pendientes" valor={dash.llegadas} ok={dash.llegadas === 0} />
            <Checklist icon={LogOut} label="Salidas pendientes" valor={dash.salidas} ok={dash.salidas === 0} />
            <Checklist icon={AlertTriangle} label="Tentativas por vencer" valor={dash.tentativas_por_vencer} ok={dash.tentativas_por_vencer === 0} />
            <Checklist icon={AlertTriangle} label="Por liquidar" valor={dash.por_liquidar} ok={dash.por_liquidar === 0} />
          </div>
          <p className="text-xs text-muted-foreground">
            Las salidas pendientes <b>bloquean</b> el cierre: hay que hacer el check-out o extender la
            estadía antes de seguir. Lo demás no bloquea, pero conviene resolverlo antes de cerrar.
          </p>
          <div className="flex justify-end">
            <Button disabled={cerrar.isPending} onClick={() => setConfirmando(true)}>
              <CalendarCheck className="h-4 w-4" /> Ejecutar cierre del {fmtFecha(dash.fecha_hotel)}
            </Button>
          </div>
        </CardContent>
      </Card>

      {errorVencidas && (
        <div className="space-y-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
          <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{errorVencidas}</span></div>
          {isAdmin() && (
            <div className="flex justify-end">
              <Button
                size="sm" variant="destructive" disabled={cerrar.isPending}
                onClick={async () => {
                  if (await dlg.confirmar('¿Forzar el cierre nocturno?', { detalle: 'Se cerrará el día SIN hacer el check-out de las salidas vencidas ni cargarles la noche: quedan en casa y hay que regularizarlas a mano.', aceptar: 'Forzar cierre', peligro: true })) cerrar.mutate(true);
                }}
              >
                Forzar cierre de todos modos
              </Button>
            </div>
          )}
        </div>
      )}

      {resultado && (
        <Card className="border-emerald-400">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Resultado del cierre</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KpiMini label="Ocupación" valor={`${num(resultado.ocupacion_pct, 1)}%`} />
            <KpiMini label="ADR" valor={money(resultado.adr)} />
            <KpiMini label="RevPAR" valor={money(resultado.revpar)} />
            <KpiMini label="Ingreso hospedaje" valor={money(resultado.ingreso_hospedaje)} />
            <KpiMini label="No-shows" valor={num(resultado.no_shows, 0)} />
            <KpiMini label="Cancelaciones (depósito)" valor={num(resultado.cancelaciones_deposito, 0)} />
            <KpiMini label="Nueva fecha de hotel" valor={fmtFecha(resultado.nueva_fecha_hotel ?? resultado.fecha)} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Historial de cierres</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {cargandoHist && <div className="py-6 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                <SortTh col="fecha" sort={sort}>Fecha</SortTh>
                <SortTh col="ocupacion" sort={sort} align="right">Ocupación</SortTh>
                <SortTh col="adr" sort={sort} align="right">ADR</SortTh>
                <SortTh col="revpar" sort={sort} align="right">RevPAR</SortTh>
                <SortTh col="ingreso" sort={sort} align="right">Ingreso hospedaje</SortTh>
                <SortTh col="noshows" sort={sort} align="right">No-shows</SortTh>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((a) => (
                <tr key={a.id} className="border-b last:border-0">
                  <td className="py-2 pr-3">{fmtFecha(a.fecha)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(a.resumen.ocupacion_pct, 1)}%</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(a.resumen.adr)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(a.resumen.revpar)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(a.resumen.ingreso_hospedaje)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(a.resumen.no_shows, 0)}</td>
                </tr>
              ))}
              {!historial.length && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Todavía no hay cierres registrados</td></tr>
              )}
            </tbody>
            {historial.length > 0 && (
              <tfoot>
                <tr className="border-t-2 bg-muted/30 font-semibold">
                  <td className="py-2 pr-3">Totales / promedio ({historial.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(totales.ocup / n, 1)}%</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(totales.adr / n)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(totales.revpar / n)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(totales.ingreso)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(totales.noshows, 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      {confirmando && (
        <Modal title="Confirmar cierre nocturno" onClose={() => setConfirmando(false)}>
          <div className="space-y-4">
            <p className="text-sm">
              Se va a ejecutar el cierre del <strong>{fmtFecha(dash.fecha_hotel)}</strong>. Esto es
              irreversible: cobra las noches de hoy al folio, vence depósitos y marca no-shows, y abre
              el día siguiente.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmando(false)}>Cancelar</Button>
              <Button disabled={cerrar.isPending} onClick={() => cerrar.mutate(false)}>
                {cerrar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirmar cierre'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Checklist({ icon: Icon, label, valor, ok }: { icon: LucideIcon; label: string; valor: number; ok: boolean }) {
  return (
    <div className={`rounded-md border p-2 ${ok ? '' : 'border-amber-400'}`}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>{valor}</div>
    </div>
  );
}

function KpiMini({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="rounded-md border p-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums">{valor}</div>
    </div>
  );
}

// Cierre automático (script 940): lo ejecuta el servidor a la hora configurada
interface EstadoAuto { activo: boolean; hora: string; zona_horaria: string; fecha_hotel: string; hora_local: string;
  log: { fecha_hotel: string; ejecutado_at: string; ok: boolean; error: string | null; ocupacion_pct: number | null; no_shows: number | null }[] }
function EstadoCierreAuto() {
  const { data } = useQuery<EstadoAuto | null>({ queryKey: [QK, 'cierre-auto'], queryFn: () => hotelRpc<EstadoAuto | null>('cierre_auto_estado', {}) });
  if (!data) return null;
  const ult = data.log[0];
  return (
    <div className={`mt-2 rounded-md border px-3 py-2 text-xs ${data.activo ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200' : 'border-border text-muted-foreground'}`}>
      {data.activo
        ? <>Cierre automático <b>activo</b>: todos los días a las {data.hora.slice(0, 5)} ({data.zona_horaria}; ahora allí son las {data.hora_local}).</>
        : <>El cierre automático está desactivado: actívelo en Configuración del hotel para que el servidor lo haga solo.</>}
      {ult && (
        <div className={`mt-1 ${ult.ok ? '' : 'font-semibold text-red-700 dark:text-red-400'}`}>
          Última corrida: {new Date(ult.ejecutado_at).toLocaleString(LOCALE)} · día {fmtFecha(ult.fecha_hotel)} · {ult.ok ? `ok (ocupación ${ult.ocupacion_pct ?? 0} %, no-shows ${ult.no_shows ?? 0})` : `NO cerró: ${ult.error}`}
        </div>
      )}
    </div>
  );
}
